import {
  keywordKey,
  TEMPLATE_KEYWORD_CLASSIFY_BATCH_SIZE,
  type TemplateKeyword,
  type TemplateKeywordCategory,
  type TemplateKeywordGeneratePool,
  toAmazonKeywordText,
} from "@adpilot/shared";

/** Tab order of ② 关键词 (Paper TP-05); 已丢弃 is set apart after a divider. */
export const KEYWORD_POOLS = [
  "core",
  "long_tail",
  "competitor_brand",
  "misspelling",
  "non_english",
  "negative",
  "discard",
] as const satisfies readonly TemplateKeywordCategory[];
export type KeywordPool = (typeof KEYWORD_POOLS)[number];

/** Pools that become keyword targets; at least one selected keyword is needed (N18). */
export const POSITIVE_POOLS = new Set<TemplateKeywordCategory>([
  "core",
  "long_tail",
  "competitor_brand",
  "misspelling",
  "non_english",
]);

export const GENERATE_POOLS = new Set<KeywordPool>([
  "competitor_brand",
  "misspelling",
  "non_english",
  "negative",
]);

export const isGeneratePool = (pool: KeywordPool): pool is TemplateKeywordGeneratePool =>
  GENERATE_POOLS.has(pool);

/** Local, not-yet-saved edits; replayed on top of the latest server keywords. */
export type KeywordEdit =
  | { type: "category"; keys: string[]; category: TemplateKeywordCategory }
  | { type: "selected"; keys: string[]; selected: boolean }
  | { type: "add"; keywords: TemplateKeyword[] };

export function applyKeywordEdits(
  keywords: readonly TemplateKeyword[],
  edits: readonly KeywordEdit[],
): TemplateKeyword[] {
  let result = [...keywords];
  for (const edit of edits) {
    if (edit.type === "add") {
      const existing = new Set(result.map((keyword) => keywordKey(keyword.text)));
      result = [
        ...edit.keywords.filter((keyword) => !existing.has(keywordKey(keyword.text))),
        ...result,
      ];
      continue;
    }
    const keys = new Set(edit.keys);
    result = result.map((keyword) => {
      if (!keys.has(keywordKey(keyword.text))) return keyword;
      if (edit.type === "selected") return { ...keyword, selected: edit.selected };
      // A user decision: AI never re-ranks it again, and it is used unless discarded.
      return {
        ...keyword,
        category: edit.category,
        source: "user",
        selected: edit.category !== "discard",
      };
    });
  }
  return result;
}

/**
 * 「添加关键词」 input (N21 / Q14): split on `,` and `，`; entries are rewritten into text
 * Amazon accepts, and unusable or duplicate (case-insensitive, also against the draft) ones
 * are skipped without telling the user.
 */
export function parseManualKeywords(input: string, existing: readonly TemplateKeyword[]): string[] {
  const seen = new Set(existing.map((keyword) => keywordKey(keyword.text)));
  const texts: string[] = [];
  for (const part of input.split(/[,，]/)) {
    const text = toAmazonKeywordText(part);
    if (!text) continue;
    const key = keywordKey(text);
    if (seen.has(key)) continue;
    seen.add(key);
    texts.push(text);
  }
  return texts;
}

export function manualKeyword(text: string, category: TemplateKeywordCategory): TemplateKeyword {
  return {
    text,
    category,
    source: "user",
    reason: null,
    selected: category !== "discard",
    metrics: {
      monthlyPurchases: null,
      monthlySearchVolume: null,
      relevance: null,
      abaMonthlyRank: null,
      ppcBid: null,
    },
  };
}

/** Manually added first (Q5), then 月购买量 descending; ties keep the server order. */
export function sortPool(keywords: readonly TemplateKeyword[]): TemplateKeyword[] {
  const purchases = (keyword: TemplateKeyword) =>
    keyword.metrics.monthlyPurchases ?? Number.NEGATIVE_INFINITY;
  return keywords
    .map((keyword, index) => ({ keyword, index }))
    .sort((a, b) => {
      const user = Number(b.keyword.source === "user") - Number(a.keyword.source === "user");
      if (user !== 0) return user;
      const diff = purchases(b.keyword) - purchases(a.keyword);
      return diff !== 0 && !Number.isNaN(diff) ? diff : a.index - b.index;
    })
    .map(({ keyword }) => keyword);
}

const CAMPAIGN_KEYWORD_LIMIT = 300;
/** 自动-紧密匹配、自动-同类商品、商品投放、商品类别关键词组 (doc §4). */
const FIXED_CAMPAIGNS = 4;

/**
 * 「将创建 N 个广告活动」 (doc §4): core × 3 + each keyword layer split per 300 + the fixed
 * layers. ponytail: ignores campaigns deleted in ③ — the preview recompiles exactly.
 */
export function countCampaigns(keywords: readonly TemplateKeyword[]): number {
  const selected = (category: TemplateKeywordCategory) =>
    keywords.filter((keyword) => keyword.selected && keyword.category === category).length;
  const split = (count: number) => Math.ceil(count / CAMPAIGN_KEYWORD_LIMIT);
  return (
    selected("core") * 3 +
    split(selected("long_tail")) +
    FIXED_CAMPAIGNS +
    split(selected("competitor_brand")) +
    split(selected("misspelling")) +
    split(selected("non_english"))
  );
}

/** Rough wall-clock guess shown in the confirm dialog / progress line. */
export function estimateClassifyMinutes(batches: number): number {
  // Mirrors the API: 3 batches in parallel, ~30 s per round of 100-keyword batches.
  return Math.max(1, Math.ceil(batches / 6));
}

export const classifyBatches = (keywordCount: number) =>
  Math.ceil(keywordCount / TEMPLATE_KEYWORD_CLASSIFY_BATCH_SIZE);
